import { ColumnDef } from '@tanstack/react-table';
import ColumnsIcon from '../../assets/icons/columns.svg?react';
import MailIcon from '../../assets/icons/mail.svg?react';
import PlusIcon from '../../assets/icons/plus.svg?react';
import TeamsIcon from '../../assets/icons/teams.svg?react';
import { IconButton } from '../../components/IconButton';
import { TableRowActions } from '../../components/tables/TableRowActions';
import { ChannelType, NotificationChannel } from '../../gql/generated';

interface ChannelColumnsOptions {
  onEdit: (channel: NotificationChannel) => void;
  onDelete: (channelId: string) => void;
  onTestNotification: (channelId: string) => void;
  loadingChannels: Set<string>;
  onColumnsClick?: () => void;
}

export function createChannelColumns({
  onEdit,
  onDelete,
  onTestNotification,
  loadingChannels,
  onColumnsClick,
}: ChannelColumnsOptions): ColumnDef<NotificationChannel>[] {
  return [
    {
      accessorKey: 'name',
      id: 'channelName',
      header: 'Channel name',
      cell: ({ row }) => (
        <div className='truncate' title={row.getValue('channelName')}>
          {row.getValue('channelName')}
        </div>
      ),
      enableSorting: true,
      enableHiding: false,
      enableResizing: true,
      enablePinning: false,
      size: 200,
    },
    {
      accessorKey: 'channelType',
      id: 'type',
      header: 'Type',
      cell: ({ row }) => {
        const type = row.getValue('type') as ChannelType;
        const getIcon = () => {
          switch (type) {
            case ChannelType.Teams:
              return <TeamsIcon className='text-primary h-4 w-4' />;
            case ChannelType.Email:
              return <MailIcon className='text-primary h-4 w-4' />;
            case ChannelType.Grouped:
              return <PlusIcon className='text-primary h-4 w-4' />;
          }
        };

        return (
          <div className='flex items-center gap-2'>
            {getIcon()}
            <span className='size-p2'>{type}</span>
          </div>
        );
      },
      enableSorting: true,
      enableResizing: true,
      enablePinning: false,
      size: 140,
    },
    {
      id: 'alarms',
      header: 'Pulse alerts',
      cell: () => {
        return (
          <div className='flex items-center justify-center'>
            <span className='size-p2 text-gray-400'>-</span>
          </div>
        );
      },
      enableSorting: false,
      enableResizing: true,
      enablePinning: false,
      size: 120,
    },

    {
      id: 'actions',
      header: () => (
        <div className='flex w-full items-center justify-center self-center'>
          <IconButton
            aria-label='Manage columns'
            icon={<ColumnsIcon width={20} height={20} />}
            onClick={onColumnsClick}
            className='text-secondary hover:text-primary h-5 w-5'
          />
        </div>
      ),
      cell: ({ row }) => {
        const channel = row.original;
        const isLoading = loadingChannels.has(channel.id);
        return (
          <div className='flex items-center justify-center'>
            <TableRowActions
              actions={[
                {
                  label: 'Edit',
                  onClick: () => onEdit(channel),
                },
                {
                  label: isLoading ? 'Sending...' : 'Send test notification',
                  onClick: () => onTestNotification(channel.id),
                  disabled: isLoading,
                },
                {
                  label: 'Delete',
                  onClick: () => onDelete(channel.id),
                  variant: 'danger' as const,
                },
              ]}
            />
          </div>
        );
      },
      enableSorting: false,
      enableResizing: false,
      enablePinning: true,
      enableHiding: false,
      size: 56,
      minSize: 56,
    },
  ];
}
